import React from 'react';

import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import FlagIcon from '@material-ui/icons/Flag';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@material-ui/lab';
import dayjs from 'dayjs';

import * as socketEvents from 'constants/socketEvents';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtualizarMensagemEditadaAtendimentoInterface,
  fetchHistoricoMensagemEditada,
  mensagensSelector,
  resetHistoricoMensagemEditada,
} from 'store/reducers/mensagens';

import DialogHeader from './DialogHeader';

type ModalHistoricoMensagemEditadaProps = {
  mensagemId: string;
  open: boolean;
  onClose: () => void;
};

export default function ModalHistoricoMensagemEditada(
  props: ModalHistoricoMensagemEditadaProps,
) {
  const { open, onClose, mensagemId } = props;
  const { palette } = useTheme();

  const dispatch = useAppDispatch();
  const { historicoMensagemEditada, loadingHistoricoMensagemEditada } =
    useAppSelector(mensagensSelector);

  const getHistoricoMensagemEditada = React.useCallback(() => {
    if (mensagemId) {
      dispatch(fetchHistoricoMensagemEditada({ mensagemId }));
    }
  }, [mensagemId, dispatch]);

  React.useEffect(() => {
    if (open) {
      getHistoricoMensagemEditada();
    }
  }, [getHistoricoMensagemEditada, open]);

  React.useEffect(() => {
    const onEdicaoMensagem = (
      data: AtualizarMensagemEditadaAtendimentoInterface,
    ) => {
      if (data.mensagem.id === mensagemId) {
        getHistoricoMensagemEditada();
      }
    };

    socket.on(socketEvents.ON_EDICAO_MENSAGEM, onEdicaoMensagem);

    return () => {
      socket.off(socketEvents.ON_EDICAO_MENSAGEM, onEdicaoMensagem);
    };
  }, [getHistoricoMensagemEditada, mensagemId]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      TransitionProps={{
        onExited: () => {
          dispatch(resetHistoricoMensagemEditada());
        },
      }}
    >
      <DialogHeader
        title="Histórico"
        description="Este é o histórico de alterações da mensagem"
        onClose={onClose}
      />

      <DialogContent style={{ marginBottom: 16 }}>
        {loadingHistoricoMensagemEditada ? (
          <Box display="flex" p={2} justifyContent="center">
            <CircularProgress size={24} color="inherit" />
          </Box>
        ) : (
          <Timeline align="alternate">
            {historicoMensagemEditada.map(mensagem => (
              <TimelineItem key={mensagem.id}>
                <TimelineOppositeContent>
                  <Typography variant="caption" color="textSecondary">
                    {dayjs(mensagem.registradoEm).format('HH:mm')}
                  </Typography>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '40px',
                      height: '40px',
                      border: `2px solid ${palette.divider}`,
                      padding: '8px',
                      borderRadius: '50%',
                    }}
                  >
                    <EditIcon color="primary" />
                  </Box>

                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent>
                  <Typography variant="body2">{mensagem.mensagem}</Typography>
                </TimelineContent>
              </TimelineItem>
            ))}

            <TimelineItem>
              <TimelineSeparator>
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '40px',
                    height: '40px',
                    border: `2px solid ${palette.divider}`,
                    padding: '8px',
                    borderRadius: '50%',
                  }}
                >
                  <FlagIcon color="primary" />
                </Box>
              </TimelineSeparator>
              <TimelineContent>
                <Typography variant="body2" color="textSecondary">
                  Início
                </Typography>
              </TimelineContent>
            </TimelineItem>
          </Timeline>
        )}
      </DialogContent>
    </Dialog>
  );
}
